import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiCommentsListResponse, ApiCommentResponse, COMMENT_CATEGORIES } from '@/types';
import { apiService } from '@/services/apiService';
import LCIcon from '@/components/common/LCIcon';
import { fmtCurrency, fmtPct, LCBtn, CLS } from '@/components/common/LCPrimitives';

const fmtTs = (ts: string) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    hour12: true,
  });

function surplusStyle(v: number | null | undefined): { color: string; borderColor: string; background: string } {
  if (v == null) return { color: '#7A7A76', borderColor: 'rgba(0,0,0,0.15)', background: 'transparent' };
  const pct = v * 100;
  if (pct > 25) return { color: '#1f8a4a', borderColor: 'rgba(31,138,74,0.35)', background: 'rgba(31,138,74,0.08)' };
  if (pct > 20) return { color: '#b6770b', borderColor: 'rgba(182,119,11,0.35)', background: 'rgba(255,191,0,0.10)' };
  if (pct > 15) return { color: '#E50025', borderColor: 'rgba(229,0,37,0.35)', background: 'rgba(229,0,37,0.07)' };
  return { color: '#B80020', borderColor: 'rgba(184,0,32,0.35)', background: 'rgba(184,0,32,0.09)' };
}

const Chip: React.FC<{ label: string; color?: string; border?: string; bg?: string }> = ({
  label, color = '#555', border = '#bbb', bg = 'transparent',
}) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    fontSize: 11, fontWeight: 600, letterSpacing: '0.03em',
    padding: '2px 8px', border: `1px solid ${border}`,
    color, background: bg, borderRadius: 999, whiteSpace: 'nowrap',
  }}>{label}</span>
);

const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
    padding: '2px 7px', background: '#EFECE8', color: '#555',
    borderRadius: 999, whiteSpace: 'nowrap', textTransform: 'uppercase',
  }}>{role}</span>
);

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
    padding: '2px 8px', border: '1px solid #6464E6',
    color: '#6464E6', background: 'rgba(100,100,230,0.06)',
    borderRadius: 999, whiteSpace: 'nowrap',
  }}>{category}</span>
);

function CommentCard({ comment }: { comment: ApiCommentResponse & { replies: ApiCommentResponse[] } }) {
  const [repliesOpen, setRepliesOpen] = useState(false);
  const hasReplies = comment.replies.length > 0;

  return (
    <div className="bg-white border border-black/[0.08] px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-semibold text-[13.5px]">{comment.user_name}</span>
        <RoleBadge role={comment.user_role} />
        <CategoryBadge category={comment.category} />
        <span className="text-[11px] text-[#7A7A76]">{fmtTs(comment.created_at)}</span>
      </div>
      <div className="text-[13px] leading-relaxed">{comment.content}</div>

      {hasReplies && (
        <div className="mt-3">
          <button
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#6464E6] bg-transparent border-0 cursor-pointer p-0 hover:underline"
            onClick={() => setRepliesOpen(v => !v)}
          >
            <LCIcon
              name="chevron"
              size={13}
              className={`transition-transform ${repliesOpen ? 'rotate-180' : ''}`}
            />
            {comment.replies.length} {comment.replies.length === 1 ? 'Reply' : 'Replies'}
          </button>

          {repliesOpen && (
            <div className="mt-3 ml-8 flex flex-col gap-2.5">
              {comment.replies.map(reply => (
                <div key={reply.comment_id} className="bg-[#FAF9F8] border border-black/[0.07] px-3.5 py-2.5">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="font-semibold text-[13px]">{reply.user_name}</span>
                    <RoleBadge role={reply.user_role} />
                    <span className="text-[11px] text-[#7A7A76]">{fmtTs(reply.created_at)}</span>
                  </div>
                  <div className="text-[13px] leading-relaxed">{reply.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const CommentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [allComments, setAllComments] = useState<ApiCommentsListResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedPortfolios, setExpandedPortfolios] = useState<Set<string>>(new Set());

  useEffect(() => { fetchAllComments(); }, []);

  const fetchAllComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllComments({});
      setAllComments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  };

  const togglePortfolio = (id: string) =>
    setExpandedPortfolios(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const filtered = useMemo(() => {
    return allComments.map(p => {
      let comments = p.comments as (ApiCommentResponse & { replies: ApiCommentResponse[] })[];
      if (categoryFilter) comments = comments.filter(c => c.category === categoryFilter);
      if (search) {
        const q = search.toLowerCase();
        comments = comments.filter(c =>
          c.content.toLowerCase().includes(q) ||
          c.user_name.toLowerCase().includes(q) ||
          (p.client_name || '').toLowerCase().includes(q) ||
          p.portfolio_id.toLowerCase().includes(q)
        );
      }
      return { ...p, comments };
    }).filter(p => p.comments.length > 0);
  }, [allComments, search, categoryFilter]);

  if (loading) return (
    <div className={`${CLS.page} flex items-center justify-center min-h-[400px]`}>
      <span className={CLS.muted}>Loading comments…</span>
    </div>
  );

  return (
    <div className={CLS.page}>
      <div className={CLS.pageHead}>
        <div>
          <h1 className={CLS.pageH1}>All Comments</h1>
          <div className={CLS.pageSub}>Every comment across all portfolios — search, filter, and jump to context.</div>
        </div>
      </div>

      {error && (
        <div className="bg-[rgba(229,0,37,0.06)] border border-[#E50025] px-4 py-3 mb-4 flex justify-between items-center">
          <span style={{ fontSize: 13 }}>{error}</span>
          <button className={CLS.iconBtn} onClick={() => setError(null)}><LCIcon name="close" size={14} /></button>
        </div>
      )}

      <div className={`${CLS.surface} p-[14px_16px] mb-4 grid [grid-template-columns:2fr_1fr] gap-3`}>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Search Comments</label>
          <div className={CLS.inputWrap}>
            <LCIcon name="search" size={14} className={CLS.inputIco} />
            <input className={`${CLS.input} pl-[38px]`} placeholder="Body, author, or client name…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Category</label>
          <select className={CLS.input} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {COMMENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 && !loading && (
        <div className={`${CLS.surface} px-6 py-10 text-center ${CLS.muted}`} style={{ fontSize: 13 }}>
          {allComments.length === 0 ? 'No comments yet.' : 'No comments match.'}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filtered.map(p => {
          const isOpen = expandedPortfolios.has(p.portfolio_id);
          const sc = p.surplus_cushion_with_cash;
          const scStyle = surplusStyle(sc);

          return (
            <div key={p.portfolio_id} className="bg-white border border-black/[0.08]">
              {/* Portfolio header */}
              <div
                className="flex items-start justify-between gap-4 px-5 py-4 cursor-pointer select-none hover:bg-[#FAF9F8] transition-colors"
                onClick={() => togglePortfolio(p.portfolio_id)}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full border border-black/[0.12] flex items-center justify-center shrink-0 mt-0.5 text-[#7A7A76]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="font-bold text-[14.5px] leading-snug">{p.client_name || '—'}</span>
                      <span className="font-mono text-[11px] text-[#7A7A76] bg-black/[0.05] px-1.5 py-0.5 rounded-sm shrink-0">{p.portfolio_id}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[12px] text-[#7A7A76] mr-0.5">{p.comments.length} {p.comments.length === 1 ? 'comment' : 'comments'}</span>
                      {sc != null && (
                        <Chip
                          label={`Surplus: ${fmtPct(sc, 2)}`}
                          color={scStyle.color}
                          border={scStyle.borderColor}
                          bg={scStyle.background}
                        />
                      )}
                      <Chip
                        label={p.is_margin_trading ? 'Margin Trading' : 'No Margin Trading'}
                        color={p.is_margin_trading ? '#E50025' : '#7A7A76'}
                        border={p.is_margin_trading ? 'rgba(229,0,37,0.35)' : 'rgba(0,0,0,0.15)'}
                        bg={p.is_margin_trading ? 'rgba(229,0,37,0.07)' : '#EFECE8'}
                      />
                      {p.total_aum != null && (
                        <Chip label={`AUM: ${fmtCurrency(p.total_aum)}`} color="#333" border="rgba(0,0,0,0.18)" bg="transparent" />
                      )}
                      {p.margin_account_equity != null && (
                        <Chip label={`Equity: ${fmtCurrency(p.margin_account_equity)}`} color="#333" border="rgba(0,0,0,0.18)" bg="transparent" />
                      )}
                      {p.lc_margin_requirement != null && (
                        <Chip label={`LC Req: ${fmtCurrency(p.lc_margin_requirement)}`} color="#E50025" border="rgba(229,0,37,0.35)" bg="rgba(229,0,37,0.07)" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 mt-0.5">
                  <LCBtn
                    variant="outline"
                    size="sm"
                    onClick={e => { e.stopPropagation(); navigate(`/portfolio/${p.portfolio_id}`); }}
                  >
                    View Portfolio
                  </LCBtn>
                  <LCIcon
                    name="chevron"
                    size={16}
                    className={`text-[#7A7A76] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {/* Comments */}
              {isOpen && (
                <div className="border-t border-black/[0.06] bg-[#FAF9F8] px-5 py-4 flex flex-col gap-3">
                  {p.comments.map(c => (
                    <CommentCard key={c.comment_id} comment={c as ApiCommentResponse & { replies: ApiCommentResponse[] }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommentsPage;
